import React from 'react';
import PropTypes from 'prop-types';
import './Colors.css';
import FrenzyMain from './FrenzyMain';
import partition from 'lodash.partition';
import DebounceableComponent from './utils/debounce';

type Props = {

};

enum GameMode {
	ATTRACT_MODE,
  IN_GAME,
	ENTER_NEW_HIGH_SCORE,
	SHOW_HIGH_SCORE_TABLE,
	ATTRACT_MODE_REQUESTED, // Stops the space bar causing instant start
}

type HighScoreEntry = {
	name: string
	score: number
}

type State = {
	gameMode: GameMode;
	newHighScoreName: string;
	newHighScore: number;
	highlightedHighScoreKey: string;
}

const ALLOWED_CHARS = /^\w{1}$/
const HIGH_SCORE_TABLE_SIZE = 8;
const MAX_HIGH_SCORE_NAME_LENGTH = 12;
const DEFAULT_AUTHOR_HIGH_SCORE = 123456;
const FRENZY_HS_MARKER="frenzyHighScores";
class FrenzyGame extends DebounceableComponent<Props, State> {
	
	private loopSubscriptionId: number;

  static contextTypes = {
    loop: PropTypes.object,
  };

	constructor(props:Props) {
		super(props);
		this.loopSubscriptionId = 0;

		this.state = {
			gameMode: GameMode.ATTRACT_MODE,	// Set this to IN_GAME for quick start
			newHighScoreName: "",
			newHighScore: 0,
			highlightedHighScoreKey: "",
		};
	}

  componentDidMount() {
    this.loopSubscriptionId = this.context.loop.subscribe(this.update);
    this.keyListener.subscribe([
      this.keyListener.SPACE,
    ]);
		document.addEventListener("keydown", this.handleHighScoreKeyDown);
  }

  componentWillUnmount() {
    this.context.loop.unsubscribe(this.loopSubscriptionId);
		this.keyListener.unsubscribe();
		document.removeEventListener("keydown", this.handleHighScoreKeyDown);
  }

  update = () => {
		if (this.debounce(
			this.state.gameMode === GameMode.ATTRACT_MODE_REQUESTED, 
			this.keyListener.SPACE, {
				gameMode: GameMode.ATTRACT_MODE
		},
		"Waiting for a fresh space")) {
		return;
	}

		if (this.state.gameMode === GameMode.ATTRACT_MODE) {
			if (this.keyListener.isDown(this.keyListener.SPACE)) {
				this.setState({ gameMode: GameMode.IN_GAME });
			}
		} else if (this.state.gameMode === GameMode.SHOW_HIGH_SCORE_TABLE) {
			if (this.keyListener.isDown(this.keyListener.SPACE)) {
				this.setState({ gameMode: GameMode.ATTRACT_MODE_REQUESTED });
			}
		}
	}

	onGameExit = () => {
		this.setState({ gameMode: GameMode.ATTRACT_MODE });
	}


	getHighScoreTableFromCookie = ():Array<HighScoreEntry> => {
 		const cookie = document.cookie;
		const cookieParts = cookie?.split(";") || [];

		const frenzyCookie = cookieParts.map((p) => p.trim()).find((p) => p.startsWith(FRENZY_HS_MARKER))
		if (frenzyCookie) {
			const frenzyCookieParts = frenzyCookie.substring(FRENZY_HS_MARKER.length + 1).split("|");
			return frenzyCookieParts.map(hsRow => {
				const hsRowParts = hsRow.split(":");
				return {
					name: hsRowParts[0],
					score: parseInt(hsRowParts[1], 10),
				}
			})
		} else {
			return [{ name: "The Author", score: DEFAULT_AUTHOR_HIGH_SCORE }]
		}
	}

	pushHighScoreTableToCookie = (hsTable: Array<HighScoreEntry>) => {
		const flattenedRows = hsTable.map(hsRow => {
			return `${hsRow.name}:${hsRow.score}`;
		})
		const newCookie = flattenedRows.join("|");

		document.cookie = `${FRENZY_HS_MARKER}=${newCookie}`;
	}

	onGameOver = (finalScore: number) => {
		const highScores = this.getHighScoreTableFromCookie();
		if (highScores.length < HIGH_SCORE_TABLE_SIZE) {
			this.setState({
				gameMode: GameMode.ENTER_NEW_HIGH_SCORE,
				newHighScore: finalScore
			});
		} else {
			const lowestExistingHighScore = highScores.reduce((lowest: number, current: HighScoreEntry) => {
				if (current.score < lowest) {
					return current.score
				}
				return lowest
			}, DEFAULT_AUTHOR_HIGH_SCORE)

			if (finalScore > lowestExistingHighScore) {
				this.setState({
					gameMode: GameMode.ENTER_NEW_HIGH_SCORE,
					newHighScore: finalScore
				});
			} else {
				// Not a high score, just show the HS table
			this.setState({
				highlightedHighScoreKey: "",
				gameMode: GameMode.SHOW_HIGH_SCORE_TABLE,
			});
		}
	}
}
	
	renderWelcomeScreen = () => {
		return (
			<div>
				<br/>
				<br/>
				<br/>
				<h1 className="Yellow">Welcome to Frenzy.js</h1>
				<br/>
				<h3 className="Cyan">Controls:&nbsp;&nbsp;</h3>
				<h4>&nbsp;&nbsp;&nbsp;&nbsp;←: Left&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</h4>
				<h4>&nbsp;&nbsp;&nbsp;&nbsp;→: Right&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</h4>
				<h4>&nbsp;&nbsp;&nbsp;&nbsp;↑: Up&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</h4>
				<h4>&nbsp;&nbsp;&nbsp;&nbsp;↓: Down&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</h4>
				<h4>Space: Draw slow/fast</h4>
				<h4>&nbsp;&nbsp;&nbsp;&nbsp;P: Pause&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;</h4>
				<br />
				<h3 className="Magenta">Hit space to begin</h3>
			</div>
		);
	};

	renderGame = () => {
		return <FrenzyMain onGameExit={this.onGameExit} onGameOver={this.onGameOver}/>;
	};

	
	handleHighScoreKeyDown = (event: KeyboardEvent) => {
		if (this.state.gameMode !== GameMode.ENTER_NEW_HIGH_SCORE) {
			return;
		}
		if (event.defaultPrevented) {
      return; // Should do nothing if the default action has been cancelled
    }
		const { newHighScoreName: oldName } = this.state;
		if (event.key === "Backspace") {
			this.setState({
				newHighScoreName: oldName.slice(0, oldName.length -1)
			});
		} else if (event.key === "Enter") {
			this.onHighScoreEntryComplete(oldName);
		} else if (event.key === " " || ALLOWED_CHARS.test(event.key)) {
			if (this.state.newHighScoreName.length < MAX_HIGH_SCORE_NAME_LENGTH) {
				this.setState({
					newHighScoreName: (oldName + event.key)
				});
			}
		}

		// event.preventDefault();
	}

	renderHighScoreRowKey = (hse: HighScoreEntry): string => {
		return `hs-${hse.score}-${hse.name}`;
	}

	onHighScoreEntryComplete = (name: string) => {
		const highScores = this.getHighScoreTableFromCookie();
		const { newHighScore } = this.state;

		const [higherScores, lowerScores] = partition(highScores, (hse: HighScoreEntry) => {
			return (hse.score) > newHighScore
		})

		const newEntry = {
			name,
			score: newHighScore
		}
		const highlightedHighScoreKey = this.renderHighScoreRowKey(newEntry);
		const newHighScores = [...higherScores, newEntry, ...lowerScores].slice(0, HIGH_SCORE_TABLE_SIZE);
		this.pushHighScoreTableToCookie(newHighScores);
		this.setState({
			newHighScore: 0,
			newHighScoreName: "",
			highlightedHighScoreKey,
			gameMode: GameMode.SHOW_HIGH_SCORE_TABLE,
		})
	}

	renderEnterHighScoreScreen() {
		return (
			<div>
				<br/>
	
				<h4 className="White" style={{backgroundColor: "red", width: "30em", height: "4em", marginLeft: "auto", marginRight: "auto", lineHeight: "4em" }}>N e w&nbsp;&nbsp;&nbsp;H i g h&nbsp;&nbsp;&nbsp;S c o r e</h4>
				<br/>
				<h4 className="Yellow">You have one of the top {HIGH_SCORE_TABLE_SIZE} scores.</h4>
				<br />
				<h4 className="Yellow">Please enter your name:</h4>
				<br />
				<br/>
				<h4 className="White" style={{backgroundColor: "red", width: "20em", height: "2em", marginLeft: "auto", marginRight: "auto", lineHeight: "2em" }}>{this.state.newHighScoreName}</h4>
				<br/>
			</div>
		);
	}

	renderHighScoreRow(hse:HighScoreEntry, highlightedHighScoreKey: string) {
		const scoreString = new String(hse.score)
		const scoreLength = scoreString.length
		const nameString = new String(hse.name)
		const nameLength = nameString.length
		const frontPad = 8 - scoreLength;
		const endPad = MAX_HIGH_SCORE_NAME_LENGTH - nameLength;
		const key = this.renderHighScoreRowKey(hse);
		return (
			<h4 key={key} className={key === highlightedHighScoreKey ? "Yellow" : "White"}>{Array.from({length: frontPad}).map(() => <>&nbsp;</>)}{scoreString} {nameString}{Array.from({length: endPad}).map(() => <>&nbsp;</>)}</h4>
		)
	}

	renderHighScoreScreen(highlightedHighScoreKey: string) {
		const highScores = this.getHighScoreTableFromCookie()
		return (
			<div>
				<br/>
	
				<h4 className="White" style={{backgroundColor: "red", width: "30em", height: "4em", marginLeft: "auto", marginRight: "auto", lineHeight: "4em" }}>H i g h&nbsp;&nbsp;&nbsp;S c o r e s</h4>
				<br/>
				{
					highScores.map((hse) => this.renderHighScoreRow(hse, highlightedHighScoreKey))
				}
				<br />
				<br/>
				<h3 className="Magenta">Hit space to restart</h3>
				<br/>
			</div>
		);
	}
		
	renderGameOverScreen = () => {
		return (
			<div>
				<br/>
				<br/>
				<br/>
				<h1 className="Yellow">GAME OVER</h1>
				<br/>
				<h4>High score table will</h4>
				<h4>eventually go here</h4>
				<br />
				<h3 className="Magenta">Hit space to restart</h3>
			</div>
		);
	};


  render() {
		const { gameMode, highlightedHighScoreKey } = this.state;
    switch (gameMode) {
			case GameMode.IN_GAME:
					return this.renderGame();
			case GameMode.ATTRACT_MODE_REQUESTED:
			case GameMode.ATTRACT_MODE:
				return this.renderWelcomeScreen();
			case GameMode.ENTER_NEW_HIGH_SCORE:
				return this.renderEnterHighScoreScreen();
			case GameMode.SHOW_HIGH_SCORE_TABLE:
				return this.renderHighScoreScreen(highlightedHighScoreKey);
			default:
				return this.renderGameOverScreen();
		}
  }
}

export default FrenzyGame;
